import { Link } from "react-router-dom";
import { CheckCircle2, Building, Calendar, Globe2, Target } from "lucide-react";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";

const STRENGTHS = [
  "Practical risk management",
  "Compliance",
  "HSE leadership",
  "Contractor assurance",
  "Management systems",
  "Continual improvement",
  "Technical expertise",
  "Client support",
];

const APPROACH = [
  {
    step: "01",
    title: "Understand",
    desc: "Understand the project, workplace, activities and risk profile.",
  },
  {
    step: "02",
    title: "Assess",
    desc: "Identify hazards and assess the effectiveness of existing controls.",
  },
  {
    step: "03",
    title: "Control",
    desc: "Develop and implement practical risk controls and safe systems of work.",
  },
  {
    step: "04",
    title: "Assure",
    desc: "Verify performance through inspections, audits, monitoring and reporting.",
  },
  {
    step: "05",
    title: "Improve",
    desc: "Use findings, trends, incidents and engagement to drive continual improvement.",
  },
];

const QUALIFICATIONS = [
  "NEBOSH Diploma — Current Study",
  "NEBOSH General Certificate",
  "NEBOSH Construction",
  "NEBOSH Process Safety Management",
  "CMI Level 5 Leadership & Project Management",
  "CMI Level 5 Management & Leadership",
  "PTLLS Level 4",
  "FPA Fire Risk Assessor",
  "BSI Environmental Management Systems Internal Auditing",
  "BOHS P402 Asbestos Surveying & Sampling",
  "CDM / UK",
  "CSCS Management",
  "SMSTS",
];

export default function About() {
  return (
    <>
      <PageMeta
        title="About SafetySymbiosis | Established UK HSE Consultancy"
        description="Established in 2014, SafetySymbiosis is a UK-registered HSE and SHEQ consultancy led by an experienced principal professional."
      />

      {/* Hero */}
      <section className="border-b border-brand-line bg-white py-14 lg:py-20">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">About Us</span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
              About SafetySymbiosis
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              SafetySymbiosis is an established UK HSE/SHEQ consultancy,
              registered in the UK since 2014. We provide practical support
              across complex projects, industrial environments and operational
              workplaces — helping our clients deliver safely, efficiently and
              responsibly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Company-first intro */}
      <section className="bg-white py-16 lg:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-20">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              An established consultancy, built on real experience
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-brand-charcoal/85">
              <p>
                Our expertise covers practical risk management, compliance, HSE
                leadership, contractor assurance, management systems and
                continual improvement. We work with clients, principal
                contractors and specialist contractors across construction,
                engineering, manufacturing, automation, data centres and other
                high-risk environments.
              </p>
              <p>
                We keep our support proportionate: effort directed where risk
                is highest, controls that work in the real world, and clear
                reporting that helps projects and operations move forward
                safely.
              </p>
              <p>
                As SafetySymbiosis grows, our model is built to scale — the
                same practical, site-focused approach delivered by additional
                HSE consultants, SHEQ professionals, auditors and trainers
                under one consistent standard.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {STRENGTHS.map((s) => (
                <div key={s} className="flex items-center gap-2.5 text-sm font-medium text-brand-navy">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-green" />
                  {s}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="card bg-brand-mist p-8">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-navy">
                Company Overview
              </h3>
              <ul className="mt-6 space-y-5">
                {[
                  { icon: Calendar, k: "Established", v: "2014 — UK registered" },
                  { icon: Building, k: "Base", v: "Manchester, United Kingdom" },
                  { icon: Globe2, k: "Markets", v: "United Kingdom · Sweden · Portugal" },
                  { icon: Target, k: "Focus", v: "HSE & SHEQ consultancy, site-based and project support" },
                ].map((f) => (
                  <li key={f.k} className="flex gap-3.5">
                    <f.icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60">
                        {f.k}
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-brand-navy">{f.v}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Our approach */}
      <section className="bg-brand-mist py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Our Approach"
            title="Our Approach"
            intro="A simple, disciplined cycle that keeps HSE support practical and effective."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {APPROACH.map((a, i) => (
              <Reveal key={a.step} delay={i * 0.06}>
                <div className="card h-full border-t-2 border-t-brand-green p-6">
                  <span className="font-heading text-3xl font-extrabold text-brand-green/25">
                    {a.step}
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold uppercase tracking-wide">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/80">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-white py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Leadership"
            title="Leadership"
            intro="The experience behind SafetySymbiosis."
          />
          <Reveal className="mt-12">
            <div className="card grid gap-8 p-8 md:grid-cols-[auto_1fr] md:p-10">
              <img
                src="/david-azor.jpg"
                alt="David Azor — Founder / Director / Principal HSE Professional, SafetySymbiosis"
                className="h-44 w-40 rounded-xl object-cover object-top md:h-60 md:w-52"
                width={848}
                height={1264}
                loading="lazy"
                data-testid="leadership-photo"
              />
              <div>
                <h3 className="font-heading text-xl font-bold text-brand-navy">David Azor</h3>
                <p className="mt-1 text-sm font-semibold text-brand-green">
                  Founder / Director / Principal HSE Professional
                </p>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-brand-charcoal/85 sm:text-base">
                  David brings more than 27 years of progressive HSE experience
                  across construction, civil engineering, industrial and process
                  environments, M&amp;E/MEICA, manufacturing, automation and
                  robotics, data centres, EV battery manufacturing, food and
                  beverage, warehousing, healthcare and renewable-energy
                  environments.
                </p>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-brand-charcoal/85 sm:text-base">
                  His experience includes client, principal-contractor and
                  specialist-contractor environments — the foundation of our
                  practical, programme-aware approach.
                </p>
                <Link
                  to="/experience"
                  className="btn-outline mt-6 !py-2.5 text-sm"
                  data-testid="about-view-professional-profile"
                >
                  View Professional Profile
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Qualifications */}
      <section className="border-t border-brand-line bg-white py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Qualifications"
            title="Selected Qualifications"
            intro="Professional qualifications held within our leadership team."
          />
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {QUALIFICATIONS.map((q, i) => (
              <Reveal key={q} delay={(i % 3) * 0.04}>
                <div className="flex items-center gap-3 rounded-lg border border-brand-line bg-brand-mist/60 px-4 py-3.5">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-green" />
                  <span className="text-sm font-medium text-brand-navy">{q}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 max-w-4xl">
            <p className="text-sm leading-relaxed text-brand-charcoal/70">
              Plus specialist training in data centres, incident prevention,
              contractor management, work at height, LOTO, electrical safety,
              fire safety, COSHH, confined spaces and other high-risk
              activities.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
